import type { AgenticLogicRuleVersionDto } from "@intelligems/ig-types";
import { useImmer } from "use-immer";

import { useDeleteAgenticLogicRuleVersion } from "@src/api/agentic-logic/delete-agentic-logic-rule-version";
import { useAgenticLogicRuleVersions } from "@src/api/agentic-logic/get-agentic-logic-rule-versions";
import { useRollbackAgenticLogicRule } from "@src/api/agentic-logic/rollback-agentic-logic-rule";
import type { WorkspaceVersion } from "@src/features/agenticCodegen/workspace/types";

import { toWorkspaceVersion } from "./ruleVersions";

export interface RuleVersionHistory {
  /** Cards, in the shell's structural shape. */
  versions: WorkspaceVersion[];
  /** The same rows unprojected, for the per-version params an expanded card shows. */
  versionDtos: AgenticLogicRuleVersionDto[];
  rollbackTargetVersion: number | null;
  openRollback: (version: number) => void;
  closeRollback: () => void;
  confirmRollback: (version: number) => void;
  deleteVersion: (version: number) => void;
  isRollingBack: boolean;
}

/**
 * A rule's version-history wiring: the list, plus rollback and delete.
 *
 * One hook for BOTH rule flavours, because a rule's publish record is a
 * rule-level fact — the version rows exist whatever the rule's execution kind,
 * and the Versions tab that renders them is the same tab on both pages. It was
 * the template branch's counterpart to what the code branch got from its
 * workspace port; the code branch reads it here too now, so the tab has one
 * source rather than one per flavour.
 *
 * Both mutations invalidate the versions list and the rule, so a config
 * buffer's baseline re-seeds on its own after a rollback and no local cache
 * surgery is needed here.
 */
export const useRuleVersionHistory = (ruleId: string): RuleVersionHistory => {
  const versionsQuery = useAgenticLogicRuleVersions(ruleId);
  const rollbackMutation = useRollbackAgenticLogicRule();
  const deleteVersionMutation = useDeleteAgenticLogicRuleVersion();
  const [rollbackTargetVersion, setRollbackTargetVersion] = useImmer<
    number | null
  >(null);

  const versionDtos = versionsQuery.data?.versions ?? [];

  return {
    versions: versionDtos.map(toWorkspaceVersion),
    versionDtos,
    rollbackTargetVersion,
    openRollback: (version) => setRollbackTargetVersion(() => version),
    closeRollback: () => setRollbackTargetVersion(() => null),
    confirmRollback: (version) => {
      rollbackMutation.mutate(
        {
          // The version travels in the BODY here, not the path.
          pathParams: { ruleId },
          queryParams: undefined,
          body: { version },
        },
        { onSuccess: () => setRollbackTargetVersion(() => null) }
      );
    },
    deleteVersion: (version) => {
      deleteVersionMutation.mutate({
        pathParams: { ruleId, version: String(version) },
        queryParams: undefined,
        body: {},
      });
    },
    isRollingBack: rollbackMutation.isPending,
  };
};
